// 🌐 프로젝트 다국어 표시 (2026-09-10 사용자 "프로젝트 내용들도 번역") — 서버가 문서마다 `tx.<lang>.<field>` 에 저장한 번역본을
//   보는 사람의 UI 언어로 골라 보여 준다. 원문 필드는 그대로이고 표시만 치환(pjText). 번역본이 없으면 원문.
//   서버 짝: server/core/pjtTx.ts. 프로젝트 언어(langs)가 1개면 서버가 번역을 만들지 않으므로 항상 원문.
import i18next from 'i18next';

export const PJ_LANGS = ['ko', 'en', 'vi', 'zh', 'es'] as const;
export type PjLang = typeof PJ_LANGS[number];
export const PJ_LANG_LABEL: Record<PjLang, string> = { ko: '한국어', en: 'English', vi: 'Tiếng Việt', zh: '中文', es: 'Español' };

export type PjTxMap = Partial<Record<string, { name?: string; title?: string; bodyText?: string; description?: string; at?: string }>>;
export interface PjTxCarrier { tx?: PjTxMap }

/** 현재 UI 언어(5개 밖이면 ko) */
export function pjLang(): PjLang {
	const l = String(i18next.language || 'ko').slice(0, 2) as PjLang;
	return (PJ_LANGS as readonly string[]).includes(l) ? l : 'ko';
}

/** 표시용 텍스트 — tx[내 언어][field] 가 있으면 그것, 없으면 원문(obj[field]). */
export function pjText(obj: PjTxCarrier | null | undefined, field: 'name' | 'title' | 'bodyText' | 'description', lang: string = pjLang()): string {
	if (!obj) return '';
	const v = obj.tx?.[lang]?.[field];
	if (typeof v === 'string' && v.trim()) return v;
	const raw = (obj as unknown as Record<string, unknown>)[field];
	return typeof raw === 'string' ? raw : '';
}

/** 🌐 모아보기 행(댓글·산출물·홈 요약 — 서버가 이름을 문자열로 펴서 보내는 목록)용 (2026-09-28 사용자 "나머지들도 전반적으로 번역 확인").
 *   서버가 같이 실어 보낸 tx(카드 제목)·pjTx(프로젝트명)·stageTx(단계명) 중 내 언어 것을 고른다. 없으면 원문. */
export interface PjAggRow { title?: string; pjName?: string; stageName?: string; tx?: PjTxMap; pjTx?: PjTxMap; stageTx?: PjTxMap }
export function pjAggText(row: PjAggRow | null | undefined, field: 'title' | 'pjName' | 'stageName', lang: string = pjLang()): string {
	if (!row) return '';
	if (field === 'title') return pjText({ title: row.title, tx: row.tx } as PjTxCarrier, 'title', lang);
	if (field === 'pjName') return pjText({ name: row.pjName, tx: row.pjTx } as PjTxCarrier, 'name', lang);
	return pjText({ name: row.stageName, tx: row.stageTx } as PjTxCarrier, 'name', lang);
}

/** 🌐 프로젝트 알림 카드 제목(`👤 {프로젝트명} · 역할 변경`)의 원문 프로젝트명을 번역본으로 (2026-09-29 사용자 "토픽방 알림도 번역된 걸로").
 *  이미 게시된 옛 카드용 — 새 카드는 서버가 번역본을 실어 보낸다. 바꾸는 자리는 `{이모지} {이름} · ` 한 곳뿐(본문·다른 위치는 건드리지 않음).
 *  긴 이름부터 본다 — 한 이름이 다른 이름의 앞부분이어도 잘못 바뀌지 않게. */
export function swapPjNameInTitle(title: string, pairs: ReadonlyArray<readonly [string, string]>): string {
	if (!title || !pairs.length) return title;
	const sorted = [...pairs].filter(([o, t]) => o && t && o !== t).sort((a, b) => b[0].length - a[0].length);
	for (const [orig, tr] of sorted) {
		const i = title.indexOf(`${orig} · `);
		// 제목 맨 앞(이모지 뒤 첫 단어) 또는 공백 뒤에서 시작해야 한다 — 다른 단어 속의 부분 일치 방지
		if (i >= 0 && (i === 0 || title[i - 1] === ' ')) return title.slice(0, i) + tr + title.slice(i + orig.length);
	}
	return title;
}
